import type * as THREE from 'three';
import type { IIndicationStrategy } from '../Indications.controller';
import type { IndicationsService } from '../Indications.service';
import { Tools } from '../ToolStateStore';
import type { AnnotationModel } from './Annotation.model';
import { SceneItemAction } from '@/engine/app/interfaces';
import type { PointerSyntheticEvent } from '@/components/viewPort/PointerSyntheticEvent';
import type { AnnotationViewModel } from './Annotation.viewModel';
import type { ObservabilityLoggerService } from '@/app.obresvability/loggerService/observabilityLogger.service.ts';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import type { ICommandProcessor } from '../../core/interfaces/Command';
import type { IValue } from '@/engine/DataFlow/Graph';
import type { IRemover } from '@/engine/UIPresentation/Interfaces';
import type { IIndicationAdder } from '../interfaces';

export class AnnotationStrategy implements IIndicationStrategy {
  private _indicationsService: IndicationsService;

  private _model?: AnnotationModel;

  private _viewModel?: AnnotationViewModel;

  private readonly _loggerService: ObservabilityLoggerService;

  private readonly _commandProcessor: ICommandProcessor;

  private readonly _planningAlgorithm: PlanningAlgorithm;

  private readonly _remover: IRemover;

  private readonly _adder: IIndicationAdder<IValue>;

  public constructor(
    indicationsService: IndicationsService,
    planningAlgorithm: PlanningAlgorithm,
    commandProcessor: ICommandProcessor,
    adder: IIndicationAdder<IValue>,
    remover: IRemover
  ) {
    this._indicationsService = indicationsService;
    this._loggerService = indicationsService.loggerService;
    this._commandProcessor = commandProcessor;
    this._planningAlgorithm = planningAlgorithm;
    this._adder = adder;
    this._remover = remover;
  }

  public async onPointAddAsync(point: THREE.Vector3, event: PointerSyntheticEvent): Promise<void> {
    await this._initializeIndication();

    if (!this._model || !this._viewModel) throw new Error('Model and ViewModel should be defined');

    this._model?.setPoint(point);
    this._viewModel?.setOriginViewPortDescriptor(event.originViewPortDescriptor);
    await this._indicationsService.finalizeIndicationAsync();
    this._loggerService.dispatchSceneItemAction(SceneItemAction.IndicationAdded, this._viewModel);
  }

  public onPointMove(/*point: THREE.Vector3*/): void {
    return;
  }

  // eslint-disable-next-line @typescript-eslint/no-misused-promises -- //TODO: [chebotarev] fix eslint no-misused-promises
  public async dispose(): Promise<void> {
    return this._indicationsService.rollbackIndicationAsync();
  }

  private _initializeIndication(): Promise<void> {
    const { model, viewModel } = this._indicationsService.createModelViewModel(Tools.Annotation);
    this._model = model as AnnotationModel;
    this._viewModel = viewModel as AnnotationViewModel;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
    this._commandProcessor.executeAnonymousCommand(
      (): Promise<undefined> => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
        this._adder.addIndication(model, viewModel);
        return Promise.resolve(undefined);
      },
      (): Promise<undefined> => {
        this._remover.removeByDescriptor(viewModel.descriptor);
        return Promise.resolve(undefined);
      }
    );
    return Promise.resolve();
  }
}
